/**
 * The Agents overview's agents (teams, src/services/agents/teams.py). Staff only, for now (the
 * API is the tenant's Admins'). Each opens in its own page (AgentTeamEditor.tsx).
 */
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import ApiService from '@/services/api.service';
import { useApiAuthReady } from '@/hooks/useApiAuthReady';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { MODELS, triggerLabel, type TeamDoc } from './catalog';
import { errorText, teamPath, type Teams } from './data';

const api = () => ApiService.getInstance();
const modelLabel = (m: string | null) => MODELS.find((x) => x.value === (m ?? 'sonnet'))?.label.split(' ')[0] ?? m;

function TeamCard({ t, waiting }: { t: TeamDoc; waiting: number }) {
  const starts = t.members.flatMap((m) => m.triggers.map((tr) => triggerLabel(tr)));
  const approvals = t.edges.filter((e) => e.gate).length;
  return (
    <Card>
      <CardHeader className="flex flex-row items-start gap-3 space-y-0">
        <div className="flex-1 space-y-1">
          <CardTitle className="text-base">
            <Link to={teamPath(t.id!)} className="hover:underline">
              {t.name}
            </Link>
          </CardTitle>
          {t.description && <CardDescription>{t.description}</CardDescription>}
        </div>
        <Button asChild size="sm" variant="outline">
          <Link to={teamPath(t.id!)}>Edit</Link>
        </Button>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex flex-wrap items-center gap-2 text-sm">
          {t.members.map((m, i) => (
            <span key={m.id ?? m.name} className="flex items-center gap-2">
              {i > 0 && <span className="text-muted-foreground">→</span>}
              <Badge variant="secondary">
                {m.name} · {modelLabel(m.model)}
              </Badge>
            </span>
          ))}
          {approvals > 0 && (
            <span className="text-xs text-muted-foreground">
              {approvals} approval step{approvals === 1 ? '' : 's'}
            </span>
          )}
        </div>
        <p className="text-xs text-muted-foreground">Starts on: {starts.join(' · ') || 'hand-offs only'}</p>
        <div className="flex gap-4 text-sm">
          <Link to={teamPath(t.id!, 'steps')} className="underline text-muted-foreground hover:text-foreground">
            Steps
          </Link>
          <Link to={teamPath(t.id!, 'runs')} className="underline text-muted-foreground hover:text-foreground">
            Runs
          </Link>
          {waiting > 0 && (
            <Link to={teamPath(t.id!, 'runs')} className="text-amber-600 hover:underline">
              {waiting} waiting for approval
            </Link>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export default function AgentTeams() {
  const apiReady = useApiAuthReady();
  const teams = useQuery<Teams>({
    queryKey: ['agent-teams'],
    queryFn: async () => (await api().request('GET', '/agent-teams')) as Teams,
    enabled: apiReady,
  });

  if (teams.isLoading || !apiReady) return <p className="text-sm text-muted-foreground">Loading…</p>;
  if (teams.error) {
    return <p className="text-sm text-destructive">{errorText(teams.error, 'Could not load agents.')}</p>;
  }
  return (
    <section className="space-y-4">
      {/* The page header (Agents.tsx) already titles and describes this list. */}
      <div className="flex items-center">
        <Button asChild className="ml-auto">
          <Link to="/agents/teams/new">
            <Plus className="h-4 w-4 mr-1" /> New agent
          </Link>
        </Button>
      </div>
      {(teams.data?.teams ?? []).map((t) => (
        <TeamCard key={t.id} t={t} waiting={teams.data?.waiting?.[t.id!] ?? 0} />
      ))}
    </section>
  );
}
